import React, { useState, useEffect, useRef, useCallback } from "react";
import "./GlitchTheme.css";

// ─── SECRET GAME ─────────────────────────────────────────────────────────────
const SECRET_PHRASES = [
  "wonder² é sobre renascer",
  "tudo que você sente é real",
  "o sonho não termina aqui",
  "obrigado por estar aqui",
  "você chegou cedo demais",
];

function TypingGame({ onComplete }: { onComplete: () => void }) {
  const [phraseIdx, setPhraseIdx] = useState(0);
  const [typed, setTyped] = useState("");
  const [done, setDone] = useState(false);
  const [shake, setShake] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const target = SECRET_PHRASES[phraseIdx];
  const isLast = phraseIdx === SECRET_PHRASES.length - 1;

  useEffect(() => {
    inputRef.current?.focus();
  }, [phraseIdx]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (done) return;
    const val = e.target.value;
    if (!target.startsWith(val)) {
      setShake(true);
      setTimeout(() => setShake(false), 400);
      return;
    }
    setTyped(val);
    if (val === target) setDone(true);
  };

  const handleNext = () => {
    if (isLast) {
      onComplete();
    } else {
      setPhraseIdx((i) => i + 1);
      setTyped("");
      setDone(false);
    }
  };

  return (
    <div className="secret-game">
      <div className="secret-game-progress">
        {SECRET_PHRASES.map((_, i) => (
          <span
            key={i}
            className={`secret-prog-dot ${
              i < phraseIdx ? "prog-done" : i === phraseIdx ? "prog-active" : ""
            }`}
          />
        ))}
      </div>
      <p className="secret-game-label">
        // TERMINAL_DECRYPT — {phraseIdx + 1}/{SECRET_PHRASES.length}
      </p>
      <div className={`secret-game-phrase ${shake ? "secret-shake" : ""}`}>
        {target.split("").map((char, i) => {
          let cls = "sc-char-pending";
          if (i < typed.length) cls = "sc-char-done";
          else if (i === typed.length) cls = "sc-char-cursor";
          return (
            <span key={i} className={cls}>
              {char}
            </span>
          );
        })}
      </div>
      <input
        ref={inputRef}
        className="secret-game-input"
        value={typed}
        onChange={handleChange}
        spellCheck={false}
        autoComplete="off"
        placeholder="comece a digitar..."
        maxLength={target.length}
      />
      {done && (
        <div className="secret-game-success">
          <span>✓ DECRIPTADO</span>
          <button className="secret-next-btn" onClick={handleNext}>
            {isLast ? "[ REVELAR ]" : "[ PRÓXIMA ]"}
          </button>
        </div>
      )}
    </div>
  );
}

function SecretReward() {
  return (
    <div className="secret-reward">
      <p className="secret-reward-label">// MENSAGEM_DESBLOQUEADA</p>
      <div className="secret-reward-inner">
        <blockquote className="secret-quote">
          "se você chegou até aqui, é porque realmente faz parte disso. wonder²
          não é só um álbum — é um lugar. obrigado por existir nele comigo."
        </blockquote>
        <p className="secret-from">— eduardo bertozzi</p>
        <p className="secret-date">// transmitido em: 08.07.2026</p>
      </div>
    </div>
  );
}

// ─── TÍTULO ANIMADO ───────────────────────────────────────────────────────────
// O wrapper externo aplica o glow via filter sem quebrar o background-clip do h1.
// Cada letra começa com opacity:0 e sobe com um pequeno bounce.

// Delay variável por letra — simula ritmo de escrita à mão
const TITLE_CHARS = [
  { char: "W", delay: 180 },
  { char: "o", delay: 110 },
  { char: "n", delay: 100 },
  { char: "d", delay: 140 },
  { char: "e", delay: 95 },
  { char: "r", delay: 120 },
  { char: "²", delay: 200 },
];

function AnimatedTitle({
  onClick,
  extraClass,
}: {
  onClick: () => void;
  extraClass?: string;
}) {
  const [visibleCount, setVisibleCount] = useState(0);

  useEffect(() => {
    if (visibleCount >= TITLE_CHARS.length) return;
    const delay = TITLE_CHARS[visibleCount].delay;
    const t = setTimeout(() => setVisibleCount((n) => n + 1), delay);
    return () => clearTimeout(t);
  }, [visibleCount]);

  const allVisible = visibleCount >= TITLE_CHARS.length;

  return (
    <div className="wonder-title-galaxy-wrap">
      <h1
        className={`wonder-title-galaxy ${extraClass ?? ""}`}
        onClick={onClick}
      >
        {TITLE_CHARS.map(({ char }, i) => (
          <span
            key={i}
            className={`title-letter ${
              char === "²" ? "title-letter-sup" : ""
            } ${i < visibleCount ? "title-letter-visible" : ""} ${
              allVisible ? "title-letter-final" : ""
            }`}
          >
            {char}
          </span>
        ))}
      </h1>
    </div>
  );
}

// ─── HOME ─────────────────────────────────────────────────────────────────────
const Home: React.FC = () => {
  const tracklistRef = useRef<HTMLDivElement>(null);
  const storeBtnRef = useRef<HTMLButtonElement>(null);

  const [secretOpen, setSecretOpen] = useState(false);
  const [secretCompleted, setSecretCompleted] = useState(false);
  const [clickCount, setClickCount] = useState(0);
  const [titleGlitch, setTitleGlitch] = useState(false);
  const clickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Timer ─────────────────────────────────────────────────
  const calcTime = () => {
    const diff = +new Date("2026-07-08T00:00:00") - +new Date();
    if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    return {
      days: Math.floor(diff / (1000 * 60 * 60 * 24)),
      hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((diff / 1000 / 60) % 60),
      seconds: Math.floor((diff / 1000) % 60),
    };
  };
  const [timeLeft, setTimeLeft] = useState(calcTime());
  useEffect(() => {
    const t = setInterval(() => setTimeLeft(calcTime()), 1000);
    return () => clearInterval(t);
  }, []);

  // ── Spotlight ─────────────────────────────────────────────
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      document.documentElement.style.setProperty(
        "--mouse-x",
        `${(e.clientX / window.innerWidth) * 100}%`
      );
      document.documentElement.style.setProperty(
        "--mouse-y",
        `${(e.clientY / window.innerHeight) * 100}%`
      );
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  // ── Magnetic button ───────────────────────────────────────
  useEffect(() => {
    const btn = storeBtnRef.current;
    if (!btn) return;
    const onMove = (e: MouseEvent) => {
      const r = btn.getBoundingClientRect();
      btn.style.setProperty(
        "--mag-x",
        `${(e.clientX - r.left - r.width / 2) * 0.25}px`
      );
      btn.style.setProperty(
        "--mag-y",
        `${(e.clientY - r.top - r.height / 2) * 0.25}px`
      );
    };
    const onLeave = () => {
      btn.style.setProperty("--mag-x", "0px");
      btn.style.setProperty("--mag-y", "0px");
    };
    btn.addEventListener("mousemove", onMove);
    btn.addEventListener("mouseleave", onLeave);
    return () => {
      btn.removeEventListener("mousemove", onMove);
      btn.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  // ── Escape fecha o secret ──────────────────────────────────
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setSecretOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // ── Click secreto (5×) ────────────────────────────────────
  const handleTitleClick = useCallback(() => {
    setClickCount((prev) => {
      const next = prev + 1;
      setTitleGlitch(true);
      setTimeout(() => setTitleGlitch(false), 300);
      if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
      clickTimerRef.current = setTimeout(() => setClickCount(0), 2000);
      if (next >= 5) {
        setSecretOpen(true);
        return 0;
      }
      return next;
    });
  }, []);

  // ── Estrelas ──────────────────────────────────────────────
  const stars = React.useMemo(
    () =>
      Array.from({ length: 120 }, (_, i) => ({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: Math.random() * 2.5 + 0.5,
        delay: Math.random() * 4,
        duration: Math.random() * 3 + 2,
      })),
    []
  );

  const scrollToTracklist = () =>
    tracklistRef.current?.scrollIntoView({ behavior: "smooth" });

  const tracks = [
    { num: "01", title: "intro", duration: "??:??" },
    { num: "02", title: "magic", duration: "??:??" },
    { num: "03", title: "stardust", duration: "??:??" },
    { num: "04", title: "cotton candy", duration: "??:??" },
    { num: "05", title: "bubblegum", duration: "??:??" },
    { num: "06", title: "wonder²", duration: "??:??" },
    { num: "07", title: "overrated", duration: "??:??" },
    { num: "08", title: "golden", duration: "??:??" },
    { num: "09", title: "softer", duration: "??:??" },
    { num: "10", title: "satellite", duration: "??:??" },
    { num: "11", title: "paper boy", duration: "??:??" },
    { num: "12", title: "kite", duration: "??:??" },
    { num: "13", title: "outro", duration: "??:??" },
  ];

  return (
    <div className="wonder-page wonder-galaxy">
      {/* Estrelas */}
      <div className="wonder-stars">
        {stars.map((s) => (
          <div
            key={s.id}
            className="wonder-star"
            style={{
              left: `${s.x}%`,
              top: `${s.y}%`,
              width: `${s.size}px`,
              height: `${s.size}px`,
              animationDelay: `${s.delay}s`,
              animationDuration: `${s.duration}s`,
            }}
          />
        ))}
      </div>

      <div className="grain-overlay" />
      <div className="wonder-nebula-layer" />
      <div className="wonder-galaxy-spotlight" />

      {/* ── HERO ────────────────────────────────────────────── */}
      <section className="wonder-hero-section">
        <div className="wonder-hero-content">
          {clickCount > 0 && clickCount < 5 && (
            <div className="secret-click-hint">
              {Array.from({ length: 5 }).map((_, i) => (
                <span
                  key={i}
                  className={`secret-dot ${
                    i < clickCount ? "secret-dot-on" : ""
                  }`}
                />
              ))}
            </div>
          )}

          <AnimatedTitle
            onClick={handleTitleClick}
            extraClass={titleGlitch ? "title-secret-glitch" : ""}
          />

          <p className="wonder-date-galaxy blur-text-reveal">08 . 07 . 2026</p>

          <div className="wonder-timer-galaxy">
            <div className="tg-block">
              <span>{String(timeLeft.days).padStart(2, "0")}</span>
              <label>dias</label>
            </div>
            <div className="tg-sep">:</div>
            <div className="tg-block">
              <span>{String(timeLeft.hours).padStart(2, "0")}</span>
              <label>horas</label>
            </div>
            <div className="tg-sep">:</div>
            <div className="tg-block">
              <span>{String(timeLeft.minutes).padStart(2, "0")}</span>
              <label>min</label>
            </div>
            <div className="tg-sep">:</div>
            <div className="tg-block">
              <span>{String(timeLeft.seconds).padStart(2, "0")}</span>
              <label>seg</label>
            </div>
          </div>

          <button
            ref={storeBtnRef}
            className="wonder-galaxy-btn magnetic"
            onClick={scrollToTracklist}
          >
            <span>EXPLORAR O SONHO</span>
          </button>
        </div>
      </section>

      {/* ── TRACKLIST ───────────────────────────────────────── */}
      <section
        className="wonder-tracklist-section wonder-tracklist-galaxy"
        ref={tracklistRef}
      >
        <div className="tracklist-galaxy-container">
          <h2 className="tracklist-title blur-text-reveal">
            TRACKLIST_UNFOLDING
          </h2>
          <div className="tracklist-grid">
            {tracks.map((track) => (
              <div key={track.num} className="track-row track-row-galaxy">
                <span className="track-number">{track.num}</span>
                <span className="track-name">{track.title}</span>
                <div className="track-line" />
                <span className="track-duration-ethereal">
                  {track.duration}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SECRET PANEL ────────────────────────────────────── */}
      {secretOpen && (
        <>
          <div
            className="secret-overlay"
            onClick={() => setSecretOpen(false)}
          />
          <div className="secret-panel">
            <div className="secret-header">
              <span className="secret-tag">// ACESSO_RESTRITO</span>
              <button
                className="secret-close"
                onClick={() => setSecretOpen(false)}
              >
                [ ESC ]
              </button>
            </div>
            <div className="secret-divider" />
            {secretCompleted ? (
              <SecretReward />
            ) : (
              <TypingGame onComplete={() => setSecretCompleted(true)} />
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default Home;
